/**
 * Logique pure de la colonne Relances de la fiche dossier (reprise de l'ancienne page /relances,
 * limitée à UN dossier). Aucun appel réseau ici : tout est testé dans caseRelances.test.ts.
 */
import { formatParisDate, formatParisTime } from "../../../lib/parisDateTime";
import { REMINDER_STEPS, reminderProgress, type ReminderCycle, type ReminderProgressView } from "../../../lib/reminderProgress";

export type CycleStatus = ReminderCycle["status"];
export type ReminderChannel = "email" | "whatsapp" | "phone";

/** Colonnes de quote_reminder_cycles lues par la colonne (mêmes que l'ancienne page). */
export interface CycleRow {
  id: string;
  case_id: string;
  thread_id: string | null;
  quote_ids: string[] | null;
  upload_group_id: string | null;
  status: CycleStatus;
  current_step: number | null;
  next_step: number | null;
  next_reminder_at: string | null;
  last_quote_sent_at: string | null;
  last_client_response_at: string | null;
  last_staff_response_at: string | null;
  last_reminder_sent_at: string | null;
  last_error: string | null;
  whatsapp_task_due_at: string | null;
  whatsapp_status: "none" | "pending" | "sent" | "skipped" | null;
  whatsapp_message: string | null;
  stopped_reason: string | null;
  rejection_reason: string | null;
  rejection_comment: string | null;
  unsubscribed_at: string | null;
  rejected_at: string | null;
  completed_at: string | null;
  paused_at: string | null;
  created_at: string;
  updated_at: string;
}

export const CYCLE_COLUMNS =
  "id, case_id, thread_id, quote_ids, upload_group_id, status, current_step, next_step, next_reminder_at, last_quote_sent_at, last_client_response_at, last_staff_response_at, last_reminder_sent_at, last_error, whatsapp_task_due_at, whatsapp_status, whatsapp_message, stopped_reason, rejection_reason, rejection_comment, unsubscribed_at, rejected_at, completed_at, paused_at, created_at, updated_at";

export interface ReminderLog {
  id: string;
  case_id: string;
  quote_id: string | null;
  channel: ReminderChannel;
  reminder_step: number | null;
  template_key: string | null;
  subject: string | null;
  email_sent: boolean | null;
  email_id: string | null;
  error_message: string | null;
  sent_at: string | null;
  created_at: string;
}

export const LOG_COLUMNS =
  "id, case_id, quote_id, channel, reminder_step, template_key, subject, email_sent, email_id, error_message, sent_at, created_at";

export interface QuoteRow {
  id: string;
  display_name: string | null;
  version: number;
  signature_token: string | null;
  signed: boolean;
  signed_at: string | null;
  file_url: string;
  amount_ht: number | null;
}

export const QUOTE_COLUMNS = "id, display_name, version, signature_token, signed, signed_at, file_url, amount_ht";

/** Champs du dossier utiles aux relances. */
export interface RelanceCase {
  status: string;
  budget: number | null;
  budget_per_person: number | null;
  participants: number | null;
  pax: number | null;
}

export const STEP_META: Record<number, { label: string; angle: string }> = {
  1: { label: "R1", angle: "Message simple" },
  2: { label: "R2", angle: "Questions / ajustements" },
  3: { label: "R3", angle: "Aide + WhatsApp" },
  4: { label: "R4", angle: "Dernier message" },
};

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  return `${formatParisDate(value)} à ${formatParisTime(value)}`;
}

export function formatMoney(value: number | null): string {
  if (value === null) return "Montant non extrait";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(value);
}

export function toProgressCycle(cycle: CycleRow): ReminderCycle {
  return {
    id: cycle.id,
    case_id: cycle.case_id,
    status: cycle.status,
    current_step: cycle.current_step,
    next_step: cycle.next_step,
    next_reminder_at: cycle.next_reminder_at,
    last_client_response_at: cycle.last_client_response_at,
    last_error: cycle.last_error,
    stopped_reason: cycle.stopped_reason,
    paused_at: cycle.paused_at,
    completed_at: cycle.completed_at,
    unsubscribed_at: cycle.unsubscribed_at,
    rejected_at: cycle.rejected_at,
    updated_at: cycle.updated_at,
    created_at: cycle.created_at,
  };
}

/** Montant HT le plus élevé des devis suivis (comme l'ancienne page), sinon null. */
export function maxQuoteAmount(quotes: QuoteRow[]): number | null {
  const amounts = quotes.map((q) => q.amount_ht).filter((a): a is number => typeof a === "number");
  return amounts.length ? Math.max(...amounts) : null;
}

/** Vue « barre R1-R4 » d'un dossier, quel que soit son statut (les relances arrêtées restent visibles). */
export function caseProgress(c: RelanceCase, cycle: CycleRow, amountHt: number | null, now = new Date()): ReminderProgressView {
  const progressCase = { status: c.status, budget: amountHt ?? c.budget, budget_per_person: c.budget_per_person, participants: c.participants, pax: c.pax };
  return reminderProgress(progressCase, toProgressCycle(cycle), now, { anyStatus: true })!;
}

/** Qui a la main : même découpage que les tuiles de l'ancienne page. */
export type OwnerKey = "you" | "running" | "stopped" | "error";
export const OWNER_META: Record<OwnerKey, { label: string }> = {
  you: { label: "vous, le client a répondu" },
  running: { label: "relances automatiques" },
  stopped: { label: "vous" },
  error: { label: "vous, envoi à corriger" },
};

export function ownerOf(view: ReminderProgressView): OwnerKey {
  return view.tone === "you" ? "you" : view.tone === "running" ? "running" : view.tone === "error" ? "error" : "stopped";
}

/** La phrase « prochaine étape » (vocabulaire de la spec : jamais « Le CRM relance »). */
export function nextStepText(cycle: CycleRow, view: ReminderProgressView): string {
  if (view.tone === "you") return "Répondez au client depuis Messages.";
  if (view.tone === "error") return "Corrigez le problème puis renvoyez la relance.";
  if (view.tone === "running") return view.callRecommended ? "Rien à faire pour l’instant. Gros devis : un appel aidera à conclure." : "Rien à faire : relances en cours.";
  if (cycle.status === "paused") return "Reprenez les relances si besoin.";
  return "Plus aucune relance prévue.";
}

/** Étape notée pour « J'ai appelé » (identique à l'ancienne page). */
export function phoneStep(cycle: Pick<CycleRow, "current_step" | "next_step">): number {
  return Math.min(Math.max(cycle.current_step || cycle.next_step || 1, 1), REMINDER_STEPS);
}

/** Actions disponibles, avec les mêmes conditions que l'ancienne page. */
export interface RelanceActions {
  canPause: boolean;
  canResume: boolean;
  canSendNext: boolean;
  canRetry: boolean;
}

export function availableActions(cycle: CycleRow, caseStatus: string, view: ReminderProgressView): RelanceActions {
  return {
    canResume: cycle.status === "paused",
    canPause: ["active", "waiting_staff", "error"].includes(cycle.status),
    canSendNext: caseStatus === "Devis envoyé" && (cycle.current_step ?? 0) < REMINDER_STEPS && view.tone !== "error",
    canRetry: view.tone === "error",
  };
}

// Mises à jour de quote_reminder_cycles : reprises telles quelles de l'ancienne page /relances.
export const resumePatch = (now = new Date()) => ({
  status: "active" as const,
  next_reminder_at: new Date(now.getTime() + 48 * 60 * 60 * 1000).toISOString(),
  paused_at: null,
  stopped_reason: null,
  last_error: null,
});
export const pausePatch = (now = new Date()) => ({
  status: "paused" as const,
  paused_at: now.toISOString(),
  stopped_reason: "manual",
  next_reminder_at: null,
});
export const sendNowPatch = (now = new Date()) => ({
  status: "active" as const,
  next_reminder_at: now.toISOString(),
  last_error: null,
});

/** Ligne reminder_logs insérée par « J'ai appelé » (le trigger SQL met alors le cycle en pause : staff_call). */
export function phoneCallLog(cycle: CycleRow, now = new Date()) {
  const quoteIds = Array.isArray(cycle.quote_ids) ? cycle.quote_ids : [];
  return {
    reminder_type: "quote_no_reply",
    case_id: cycle.case_id,
    quote_id: quoteIds[0] ?? null,
    channel: "phone" as const,
    reminder_step: phoneStep(cycle),
    template_key: "quote_reminder_phone_call_done_v1",
    subject: "Appel de relance effectué",
    quote_ids: quoteIds,
    upload_group_id: cycle.upload_group_id,
    email_sent: true,
    sent_at: now.toISOString(),
  };
}

/** Échecs d'envoi encore d'actualité : un échec suivi d'un envoi réussi de la même étape (même canal) est réglé. */
export function unresolvedFailures(logs: ReminderLog[]): ReminderLog[] {
  const at = (log: ReminderLog) => log.sent_at || log.created_at;
  return logs
    .filter((log) => log.channel !== "phone" && !log.email_sent)
    .filter(
      (failed) =>
        !logs.some(
          (ok) =>
            ok.email_sent &&
            ok.channel === failed.channel &&
            ok.reminder_step === failed.reminder_step &&
            at(ok) >= at(failed)
        )
    )
    .sort((a, b) => at(b).localeCompare(at(a)));
}

/** Calendrier R1-R4 : état de chaque relance e-mail. */
export type StepState = "sent" | "failed" | "next" | "upcoming" | "cancelled";
export interface StepRow {
  step: number;
  label: string;
  angle: string;
  state: StepState;
  text: string;
}

export function stepRows(cycle: CycleRow, logs: ReminderLog[], view: ReminderProgressView): StepRow[] {
  return Array.from({ length: REMINDER_STEPS }, (_, i) => {
    const step = i + 1;
    const meta = STEP_META[step];
    const stepLogs = logs.filter((log) => log.reminder_step === step && log.channel === "email");
    const sent = stepLogs.find((log) => log.email_sent);
    const failed = stepLogs.find((log) => !log.email_sent && log.error_message);
    const isNext = cycle.next_step === step && cycle.status === "active" && view.tone === "running";
    let state: StepState;
    let text: string;
    if (sent) {
      state = "sent";
      text = `Envoyée le ${formatDateTime(sent.sent_at || sent.created_at)}`;
    } else if (failed) {
      state = "failed";
      text = `Non partie : ${failed.error_message}`;
    } else if (view.tone === "error" && cycle.next_step === step) {
      state = "failed";
      text = `Non partie : ${cycle.last_error || "erreur d’envoi"}`;
    } else if (isNext) {
      state = "next";
      text = cycle.next_reminder_at ? `Prévue le ${formatDateTime(cycle.next_reminder_at)}` : "Prochaine";
    } else if (view.tone === "running" || view.tone === "error") {
      // En erreur, les relances suivantes partiront après le renvoi.
      state = "upcoming";
      text = "À venir";
    } else {
      state = "cancelled";
      text = "Ne partira pas";
    }
    return { step, label: meta.label, angle: meta.angle, state, text };
  });
}

/** Ligne WhatsApp du calendrier (null si aucune tâche WhatsApp sur ce cycle). */
export function whatsappRow(cycle: CycleRow, logs: ReminderLog[]): { state: "pending" | "sent" | "skipped"; text: string } | null {
  const log = logs.find((l) => l.channel === "whatsapp" && l.email_sent);
  if (cycle.whatsapp_status === "sent" || log) {
    return { state: "sent", text: log ? `Envoyé le ${formatDateTime(log.sent_at || log.created_at)}` : "Envoyé" };
  }
  if (cycle.whatsapp_status === "pending") {
    return { state: "pending", text: cycle.whatsapp_task_due_at ? `Prévu le ${formatDateTime(cycle.whatsapp_task_due_at)}` : "Prévu" };
  }
  if (cycle.whatsapp_status === "skipped") return { state: "skipped", text: "Annulé (relances arrêtées ou client a répondu)" };
  return null;
}

/** Historique unifié CRM / Vous / Client du dossier. */
export type HistoryWho = "CRM" | "Vous" | "Client";
export type HistoryFilter = "all" | HistoryWho;
export interface HistoryEntry {
  id: string;
  who: HistoryWho;
  text: string;
  at: string;
  failed?: boolean;
}

const STOP_TEXT: Record<string, { who: HistoryWho; text: string }> = {
  staff_email: { who: "Vous", text: "Relances arrêtées : e-mail envoyé par l’équipe" },
  staff_call: { who: "Vous", text: "Relances arrêtées : appel noté" },
  case_moved: { who: "Vous", text: "Relances arrêtées : carte déplacée" },
  manual: { who: "Vous", text: "Relances mises en pause à la main" },
  signed: { who: "Client", text: "Devis signé : relances terminées" },
  max_steps: { who: "CRM", text: "4 relances envoyées sans réponse" },
};

export function buildHistory(cycle: CycleRow | null, logs: ReminderLog[]): HistoryEntry[] {
  const entries: HistoryEntry[] = logs
    .filter((log) => log.sent_at || log.created_at)
    .map((log) => {
      const at = (log.sent_at || log.created_at) as string;
      if (log.channel === "phone") return { id: log.id, who: "Vous" as const, text: "Appel noté", at };
      const channel = log.channel === "whatsapp" ? "WhatsApp" : "E-mail";
      const step = `R${log.reminder_step ?? "?"}`;
      if (!log.email_sent) {
        return { id: log.id, who: "CRM" as const, text: `${channel} ${step} non parti${log.error_message ? ` : ${log.error_message}` : ""}`, at, failed: true };
      }
      return { id: log.id, who: "CRM" as const, text: `${channel} ${step} envoyé`, at };
    });
  if (cycle) {
    if (cycle.last_quote_sent_at) entries.push({ id: "quote", who: "Vous", text: "Devis envoyé : relances programmées", at: cycle.last_quote_sent_at });
    if (cycle.last_client_response_at) entries.push({ id: "client", who: "Client", text: "Le client a répondu", at: cycle.last_client_response_at });
    if (cycle.status === "paused" && cycle.paused_at) {
      const stop = (cycle.stopped_reason && STOP_TEXT[cycle.stopped_reason]) || { who: "Vous" as const, text: "Relances mises en pause" };
      // L'appel est déjà dans les logs : on n'ajoute l'arrêt que pour les autres raisons.
      if (cycle.stopped_reason !== "staff_call") entries.push({ id: "stop", ...stop, at: cycle.paused_at });
    }
    if (cycle.status === "completed" && cycle.completed_at) {
      const stop = (cycle.stopped_reason && STOP_TEXT[cycle.stopped_reason]) || { who: "CRM" as const, text: "Relances terminées" };
      entries.push({ id: "done", ...stop, at: cycle.completed_at });
    }
    if (cycle.unsubscribed_at) entries.push({ id: "unsub", who: "Client", text: "Le client ne veut plus de relances", at: cycle.unsubscribed_at });
    if (cycle.rejected_at) entries.push({ id: "rejected", who: "Client", text: "Devis refusé par le client", at: cycle.rejected_at });
  }
  return entries.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

export function filterHistory(entries: HistoryEntry[], filter: HistoryFilter): HistoryEntry[] {
  return filter === "all" ? entries : entries.filter((entry) => entry.who === filter);
}

export function historyCounts(entries: HistoryEntry[]): Record<HistoryFilter, number> {
  const counts: Record<HistoryFilter, number> = { all: entries.length, CRM: 0, Vous: 0, Client: 0 };
  for (const entry of entries) counts[entry.who] += 1;
  return counts;
}

/** Les lots fusionnés sont retriés comme Postgres : récents d'abord, NULL en tête. */
export function sortLogs(logs: ReminderLog[]): ReminderLog[] {
  return [...logs].sort((a, b) => (b.sent_at ?? "￿").localeCompare(a.sent_at ?? "￿"));
}
